import {
  MessageSquare,
  Clock,
  MoreVertical,
  Trash2,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { HistoryConversation } from '@/types/misc';
import { Link } from 'react-router-dom';

interface ConversationCardProps {
  conversation: HistoryConversation;
  onDelete: (conversationId: string) => void;
  onRename: (conversationId: string, newTitle: string) => void;
  isEditing: boolean;
}

export function ConversationCard({
  conversation,
  onDelete,
  onRename,
  isEditing,
}: ConversationCardProps) {
  const { t } = useTranslation('common');
  return (
    <div className="group relative">
      <Link to={`/editor/${conversation.id}`}>
        <div
          className="flex h-auto w-full items-start justify-between rounded-md px-3 py-3 transition-colors duration-200 ease-out hover:bg-synergy-neutral-900"
          onClick={(event) => {
            if (isEditing) {
              event.stopPropagation();
            }
          }}
        >
          <div className="min-w-0 flex-1 space-y-2 text-left">
            <div className="flex items-center gap-3">
              <h3 className="line-clamp-1 text-wrap break-all text-base font-medium text-synergy-text-primary">
                {conversation.title}
              </h3>
            </div>
            <div className="flex items-center gap-4 text-sm font-normal text-synergy-text-tertiary">
              <span className="flex items-center">
                <Clock className="mr-1 h-3 w-3 text-synergy-text-tertiary" />
                {formatDistanceToNow(new Date(conversation.updated_at), {
                  addSuffix: true,
                  locale: ja,
                })}
              </span>
              <span className="flex items-center">
                <MessageSquare className="mr-1 h-3 w-3 text-synergy-text-tertiary" />
                {t('chat.messagesCount', { count: conversation.message_count })}
              </span>
            </div>
          </div>
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <AlertDialog>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 w-8 p-0 transition-colors duration-200 ease-out hover:bg-synergy-neutral-800"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4 text-synergy-text-secondary" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <AlertDialogTrigger
                  asChild
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuItem className="font-semibold text-brand-indigo hover:cursor-pointer hover:bg-brand-indigo hover:text-brand-cream focus:bg-brand-indigo focus:text-brand-cream">
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('dialog.delete')}
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onRename(conversation.id, conversation.title);
                  }}
                  className="text-synergy-text-primary hover:cursor-pointer hover:bg-synergy-neutral-900 hover:text-synergy-text-primary focus:bg-synergy-neutral-900 focus:text-synergy-text-primary"
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {t('history.rename')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialogContent className="border-2 border-brand-indigo bg-k-card">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display text-brand-indigo">
                  {t('history.deleteConversationTitle')}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t('history.deleteConversationDesc')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={(e) => e.stopPropagation()}>
                  {t('dialog.cancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(conversation.id);
                  }}
                  className="bg-brand-indigo text-brand-cream hover:bg-brand-indigo-hover"
                >
                  {t('dialog.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Link>
    </div>
  );
}
